import React, { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView, useMotionValue, useTransform, animate } from 'framer-motion';

const dimensions = [
  {
    id: 'tech',
    title: 'Syntax & Systems',
    subtitle: 'Tech Explorations',
    description:
      "At 19, juggling ACCA and an undergrad degree, I quietly started learning to code, not to switch careers, but because I wanted to know what it felt like to build something. I'm not the kind of developer who conjures magic from thin air. But now, AI-assisted tools amplify everything I know, making it easy for me weave magic. I think in terms of creating solutions for consulting professions in ways that a pure tech soul would not intuitively understand. I sit at the intersection of  pure consulting  and creative technology building, capable of envisioning and building things neither extreme can do alone.",
    color: 'primary',
    textClass: 'text-primary',
    link: '/blog',
  },
  {
    id: 'rabbit-hole',
    title: 'The Rabbit Hole Effect',
    subtitle: 'Design Thinking Blog',
    description:
      "I naturally notice problems and inefficiencies around me, perhaps an occupational hazard of being an internal auditor. And once I notice something, I can't help but dive in: researching, validating, and obsessing over what a better version could look like. Over time, this has given me the ability to surface industry problems that aren't easily visible, and think through solutions that are both structured and creative. I document these explorations in my blog, The Rabbit Hole Effect.",
    color: 'secondary',
    textClass: 'text-secondary',
    link: '/blog',
  },
  {
    id: 'audit',
    title: 'The Third Line of Defense',
    subtitle: 'My Daily Job',
    description:
      "My role as an internal auditor is to be the third line of defense, which means I play devil's advocate with systems and processes, so the company never has to deal with real devils later. This profession has shaped how I think deeply, and I've had the fortune of working through genuinely complex, real-world problems and inefficiencies, a few of which are captured below.",
    color: 'accent',
    textClass: 'text-accent',
    link: '#experience',
  },
  {
    id: 'indic',
    title: 'I am an Indic Soul',
    subtitle: 'Art, Literature & Culture',
    description:
      "Outside of work, I'm deeply enthusiastic about anything and everything Indian. Indic history, architecture, astronomy, theology, philosophy. I find myself endlessly drawn to the breadth of it all. What fascinates me most is the paradoxical variance and oneness within the Indic world, from Kannada literature to Sanskrit texts, so seemingly different, yet so fundamentally the same. Each tradition carries lived experiences of history that feel far more personally relevant to me than the Western philosophical canon. Though I'll admit, I did have a phase where my entire intellectual identity was built around Western philosophers. We don't talk about that era much. (lol)\n\nCarnatic and Hindustani music are close to my heart, and the traditions of the Bhakti movement have been a quiet but lasting influence. I express that love through Sri Khol and the Guitar, two very different instruments that somehow feel like home. If you ever want to geek out about any of this, hit me up, though consider yourself warned, I have never once kept these conversations short.",
    color: 'ochre',
    textClass: 'text-[#D4A017]',
    link: '#connect',
    linkLabel: 'Connect with me',
  },
];

/* ──────────────────────────────────────────────
   1. VENN DIAGRAM  –  Syntax & Systems
   Two worlds merging: Code + Risk Thinking
   ────────────────────────────────────────────── */
/*
  Single progress value (0→1→1→0) drives BOTH circles so they
  are mathematically locked together. No separate repeat loops.
  Cycle: 1 s merge → 3 s hold → 1 s separate = 5 s
*/
function VennDiagramAnimation({ isInView }) {
  const [hovered, setHovered] = useState(false);
  const progress = useMotionValue(0);

  const leftApart = 90;
  const leftMerged = 170;
  const rightApart = 310;
  const rightMerged = 230;

  const leftX = useTransform(progress, [0, 1], [0, leftMerged - leftApart]);
  const rightX = useTransform(progress, [0, 1], [0, rightMerged - rightApart]);
  const revealOpacity = useTransform(progress, [0, 0.85, 1], [0, 0, 0.4]);
  const markerOpacity = useTransform(progress, [0, 0.9, 1], [0, 0, 0.85]);

  useEffect(() => {
    if (!isInView) return;
    const ctrl = animate(progress, [0, 1, 1, 0], {
      duration: 5,
      times: [0, 0.2, 0.8, 1],
      repeat: Infinity,
      ease: 'easeInOut',
    });
    return () => ctrl.stop();
  }, [isInView, progress]);

  const codeSymbols = [
    { text: '{ }', dx: -50, y: 115 },
    { text: '</>', dx: -65, y: 150 },
    { text: 'fn()', dx: -42, y: 185 },
  ];
  const riskSymbols = [
    { text: '§', dx: 45, y: 115 },
    { text: '⚖', dx: 60, y: 150 },
    { text: '∑', dx: 40, y: 185 },
  ];

  return (
    <svg
      viewBox="0 0 430 300"
      className="w-full max-w-xs select-none"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* LEFT group — driven by leftX */}
      <motion.g style={{ x: leftX }}>
        <circle
          cx={leftApart} cy={145} r={95}
          fill="rgba(224,122,95,0.1)"
          stroke="#E07A5F" strokeWidth={1.5}
        />
        {codeSymbols.map((s, i) => (
          <text
            key={`c${i}`}
            x={leftApart + s.dx} y={s.y}
            textAnchor="middle" fill="#E07A5F"
            fontSize={11} fontFamily="JetBrains Mono, monospace"
            opacity={0.5}
          >
            {s.text}
          </text>
        ))}
      </motion.g>

      {/* RIGHT group — driven by rightX */}
      <motion.g style={{ x: rightX }}>
        <circle
          cx={rightApart} cy={145} r={95}
          fill="rgba(61,64,91,0.1)"
          stroke="#3D405B" strokeWidth={1.5}
        />
        {riskSymbols.map((s, i) => (
          <text
            key={`r${i}`}
            x={rightApart + s.dx} y={s.y}
            textAnchor="middle" fill="#3D405B"
            fontSize={13}
            opacity={0.5}
          >
            {s.text}
          </text>
        ))}
      </motion.g>

      {/* Static labels */}
      <text x={leftApart} y={260} textAnchor="middle" fill="#E07A5F"
        fontSize={11} fontWeight={600} fontFamily="Manrope, sans-serif" opacity={0.8}>
        Technology
      </text>
      <text x={rightApart} y={253} textAnchor="middle" fill="#3D405B"
        fontSize={11} fontWeight={600} fontFamily="Manrope, sans-serif" opacity={0.8}>
        <tspan x={rightApart} dy={0}>Internal Audit,</tspan>
        <tspan x={rightApart} dy={13}>Risk Advisory & Consulting</tspan>
      </text>

      {/* Intersection highlight */}
      <motion.ellipse
        cx={200} cy={145} rx={38} ry={78}
        fill="rgba(224,122,95,0.2)"
        style={{ opacity: revealOpacity }}
      />

      {/* "I am here" marker */}
      <motion.g style={{ opacity: markerOpacity }}>
        <motion.circle
          cx={200} cy={145} r={7} fill="#C0392B"
          animate={hovered ? { r: [7, 9, 7], opacity: [1, 0.7, 1] } : {}}
          transition={{ repeat: Infinity, duration: 1.2 }}
        />
        <text x={200} y={172} textAnchor="middle" fill="#3D405B"
          fontSize={12} fontWeight={700} fontFamily="Manrope, sans-serif" fontStyle="italic">
          I am here!
        </text>
      </motion.g>
    </svg>
  );
}

/* ──────────────────────────────────────────────
   2. RABBIT HOLE  –  Looping: rabbit hops in from left,
   emerges right with carrot + smile, holds 0.5 s, repeats.
   Uses a phase state-machine so React stays in control.
   ────────────────────────────────────────────── */
const RABBIT_PHASES = {
  WAIT: { dur: 500 },
  DIVE: { dur: 800 },
  INSIDE: { dur: 250 },
  EMERGE: { dur: 700 },
  SMILE: { dur: 600 },
  FADEOUT: { dur: 200 },
};
const PHASE_ORDER = ['WAIT', 'DIVE', 'INSIDE', 'EMERGE', 'SMILE', 'FADEOUT'];

function RabbitHoleAnimation({ isInView }) {
  const [hovered, setHovered] = useState(false);
  const [phase, setPhase] = useState('WAIT');

  useEffect(() => {
    if (!isInView) return;
    let timeout;
    let idx = 0;
    const next = () => {
      idx = (idx + 1) % PHASE_ORDER.length;
      const p = PHASE_ORDER[idx];
      setPhase(p);
      timeout = setTimeout(next, RABBIT_PHASES[p].dur);
    };
    timeout = setTimeout(next, RABBIT_PHASES.WAIT.dur);
    return () => clearTimeout(timeout);
  }, [isInView]);

  const hasCarrot = phase === 'EMERGE' || phase === 'SMILE' || phase === 'FADEOUT';

  const BODY = '#EEDCC8';
  const BODY_SHADE = '#DFC9AD';
  const EAR_INNER = '#F0B8A8';
  const NOSE_C = '#E8A090';
  const CARROT_C = '#E07A5F';
  const LEAF_C = '#81B29A';
  const EYE_C = '#3D405B';

  const rabbitPos = {
    WAIT:    { x: 0,   y: 0,   scale: 1,    opacity: 1 },
    DIVE:    { x: 80,  y: 28,  scale: 0.2,  opacity: 0 },
    INSIDE:  { x: 80,  y: 28,  scale: 0.2,  opacity: 0 },
    EMERGE:  { x: 160, y: 0,   scale: 1,    opacity: 1 },
    SMILE:   { x: 160, y: 0,   scale: 1,    opacity: 1 },
    FADEOUT: { x: 160, y: 0,   scale: 1,    opacity: 0 },
  };

  const pos = rabbitPos[phase];

  return (
    <motion.svg
      viewBox="0 0 320 230"
      className="w-full max-w-sm select-none"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Ground line */}
      <motion.path
        d="M15,188 Q160,180 305,188"
        fill="none"
        stroke="#D4CFC4"
        strokeWidth={2}
        initial={{ pathLength: 0 }}
        animate={isInView ? { pathLength: 1 } : { pathLength: 0 }}
        transition={{ duration: 0.5 }}
      />

      {/* Hole at center */}
      <motion.ellipse
        cx={160} cy={186} rx={44} ry={13}
        fill="#3D405B"
        initial={{ scale: 0 }}
        animate={isInView ? { scale: 1 } : { scale: 0 }}
        transition={{ duration: 0.3, delay: 0.1 }}
      />
      {[30, 18, 9].map((rx, i) => (
        <motion.ellipse
          key={i}
          cx={160} cy={186}
          rx={rx} ry={rx * 0.3}
          fill="none"
          stroke={`rgba(61,64,91,${0.45 - i * 0.12})`}
          strokeWidth={0.7}
          initial={{ scale: 0 }}
          animate={isInView ? { scale: 1, rotate: [0, 360] } : { scale: 0 }}
          transition={{
            scale: { duration: 0.3, delay: 0.2 + i * 0.08 },
            rotate: { repeat: Infinity, duration: hovered ? 5 + i : 12 + i * 3, ease: 'linear' },
          }}
          style={{ transformOrigin: '160px 186px' }}
        />
      ))}

      {/*
        Rabbit group. Base translate puts (0,0) at left-of-hole position.
        x=0  → left of hole  (SVG x ≈ 80)
        x=80 → over the hole (SVG x ≈ 160)
        x=160→ right of hole  (SVG x ≈ 240)
      */}
      <g transform="translate(80,158)">
        <motion.g
          initial={{ x: 0, y: 0, scale: 1, opacity: 1, rotate: 0 }}
          animate={{
            x:       phase === 'DIVE'   ? [0, 10, 55, 80]
                   : phase === 'EMERGE' ? [80, 110, 148, 160]
                   : pos.x,
            y:       phase === 'DIVE'   ? [0, -52, -46, pos.y]
                   : phase === 'EMERGE' ? [28, -42, -5, pos.y]
                   : pos.y,
            scale:   phase === 'DIVE'   ? [1, 1.12, 0.85, 0.2]
                   : phase === 'EMERGE' ? [0.2, 1.1, 1.02, 1]
                   : pos.scale,
            rotate:  phase === 'DIVE'   ? [0, -15, 25, 70]
                   : phase === 'EMERGE' ? [40, -10, 3, 0]
                   : 0,
            opacity: phase === 'DIVE'   ? [1, 1, 1, 0]
                   : phase === 'EMERGE' ? [0, 1, 1, 1]
                   : pos.opacity,
          }}
          transition={{
            duration: RABBIT_PHASES[phase].dur / 1000,
            ease: phase === 'DIVE' ? [0.4, 0, 0.9, 0.6] : phase === 'EMERGE' ? 'easeOut' : 'easeInOut',
          }}
        >
          {/* Body */}
          <ellipse cx={0} cy={9} rx={13} ry={17} fill={BODY} />
          <ellipse cx={0} cy={9} rx={10} ry={14} fill={BODY_SHADE} opacity={0.3} />
          {/* Head */}
          <circle cx={0} cy={-13} r={11} fill={BODY} />
          {/* Ears */}
          <ellipse cx={-7} cy={-31} rx={3.5} ry={12} fill={BODY} />
          <ellipse cx={-7} cy={-31} rx={1.8} ry={8.5} fill={EAR_INNER} />
          <ellipse cx={7} cy={-31} rx={3.5} ry={12} fill={BODY} />
          <ellipse cx={7} cy={-31} rx={1.8} ry={8.5} fill={EAR_INNER} />
          {/* Hands */}
          <ellipse cx={-14} cy={5} rx={3.5} ry={4} fill={BODY} />
          <ellipse cx={14} cy={5} rx={3.5} ry={4} fill={BODY} />
          {/* Legs */}
          <ellipse cx={-6} cy={26} rx={4} ry={5} fill="white" />
          <ellipse cx={6} cy={26} rx={4} ry={5} fill="white" />

          {/* ── Neutral face ── */}
          {!hasCarrot && (
            <>
              <circle cx={-5} cy={-15} r={2.2} fill="white" />
              <circle cx={5} cy={-15} r={2.2} fill="white" />
              <circle cx={-4.5} cy={-14.5} r={1} fill={EYE_C} />
              <circle cx={5.5} cy={-14.5} r={1} fill={EYE_C} />
              <ellipse cx={0} cy={-9} rx={1.8} ry={1.3} fill={NOSE_C} />
              <line x1={-3} y1={-6} x2={3} y2={-6} stroke={EYE_C} strokeWidth={0.8} strokeLinecap="round" />
            </>
          )}

          {/* ── Happy face + carrot ── */}
          {hasCarrot && (
            <>
              <path d="M-7.5,-16 Q-5,-18.5 -2.5,-16" fill="none" stroke={EYE_C} strokeWidth={1.2} strokeLinecap="round" />
              <path d="M2.5,-16 Q5,-18.5 7.5,-16" fill="none" stroke={EYE_C} strokeWidth={1.2} strokeLinecap="round" />
              <circle cx={-10} cy={-12} r={2.5} fill={EAR_INNER} opacity={0.35} />
              <circle cx={10} cy={-12} r={2.5} fill={EAR_INNER} opacity={0.35} />
              <ellipse cx={0} cy={-10} rx={1.8} ry={1.3} fill={NOSE_C} />
              <path d="M-5,-6.5 Q0,-2 5,-6.5" fill="none" stroke={EYE_C} strokeWidth={1} strokeLinecap="round" />
              {/* Carrot held by right hand */}
              <g transform="translate(14,-2)">
                <path d="M0,0 L8,10 L0,14 Z" fill={CARROT_C} />
                <path d="M0,0 L4,-1 L8,10 Z" fill={CARROT_C} opacity={0.7} />
                <line x1={1.5} y1={4} x2={3} y2={7} stroke="rgba(0,0,0,0.1)" strokeWidth={0.5} />
                <path d="M0,0 Q-4,-7 -1,-10" fill="none" stroke={LEAF_C} strokeWidth={1.5} strokeLinecap="round" />
                <path d="M0,0 Q3,-6 1,-10" fill="none" stroke={LEAF_C} strokeWidth={1.5} strokeLinecap="round" />
                <path d="M0,0 Q-1,-5 -4,-8" fill="none" stroke={LEAF_C} strokeWidth={1} strokeLinecap="round" opacity={0.6} />
              </g>
            </>
          )}
        </motion.g>
      </g>

      {/* Label */}
      <motion.text
        x={160} y={218}
        textAnchor="middle"
        fill="#3D405B"
        fontSize={10}
        fontFamily="Manrope, sans-serif"
        fontStyle="italic"
        initial={{ opacity: 0 }}
        animate={isInView ? { opacity: 0.6 } : { opacity: 0 }}
        transition={{ delay: 1 }}
      >
        problems in · insights out
      </motion.text>
    </motion.svg>
  );
}

/* ──────────────────────────────────────────────
   3. THREE SHIELDS  –  Third Line of Defense
   Arrows pass through first two, deflect off third
   ────────────────────────────────────────────── */
function ThirdLineAnimation({ isInView }) {
  const [hovered, setHovered] = useState(false);

  const walls = [
    { x: 90, opacity: 0.2, w: 6, label: '1st Line' },
    { x: 150, opacity: 0.4, w: 8, label: '2nd Line' },
    { x: 210, opacity: 1, w: 12, label: '3rd Line' },
  ];

  return (
    <motion.svg
      viewBox="0 0 360 240"
      className="w-full max-w-xs select-none"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Wall labels */}
      {walls.map((w, i) => (
        <motion.text
          key={`lbl-${i}`}
          x={w.x}
          y={25}
          textAnchor="middle"
          fill={i === 2 ? '#81B29A' : '#6B6B6B'}
          fontSize={i === 2 ? 11 : 9}
          fontWeight={i === 2 ? 700 : 400}
          fontFamily="Manrope, sans-serif"
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : { opacity: 0 }}
          transition={{ delay: 0.5 + i * 0.5 }}
        >
          {w.label}
        </motion.text>
      ))}

      {/* Shield walls */}
      {walls.map((w, i) => (
        <motion.rect
          key={`wall-${i}`}
          x={w.x - w.w / 2}
          y={35}
          width={w.w}
          height={150}
          rx={w.w / 2}
          fill={`rgba(129,178,154,${w.opacity})`}
          stroke="#81B29A"
          strokeWidth={i === 2 ? 2 : 1}
          initial={{ scaleY: 0, opacity: 0 }}
          animate={
            isInView
              ? {
                  scaleY: 1,
                  opacity: 1,
                }
              : { scaleY: 0, opacity: 0 }
          }
          transition={{
            scaleY: { duration: 0.5, delay: i * 0.5 },
            opacity: { duration: 0.3, delay: i * 0.5 },
          }}
          style={{ transformOrigin: `${w.x}px 110px` }}
        />
      ))}

      {/* 3rd wall glow pulse on hover */}
      {hovered && (
        <motion.rect
          x={204 - 8}
          y={35}
          width={20}
          height={150}
          rx={10}
          fill="none"
          stroke="#81B29A"
          strokeWidth={2}
          animate={{
            opacity: [0, 0.6, 0],
            width: [14, 26, 14],
            x: [207, 197, 207],
          }}
          transition={{ repeat: Infinity, duration: 1.2 }}
        />
      )}

      {/* Looping arrows: fly in → hit 3rd wall → spark → bounce back */}
      {[75, 110, 145].map((arrowY, ai) => {
        const cyc = 2.8;
        const stagger = 1.8 + ai * 0.9;
        const wallHit = 208;
        const origin = 15;

        return (
          <g key={`arrow-${ai}`}>
            {/* Incoming arrowhead → */}
            <motion.polygon
              points="0,-4 9,0 0,4"
              fill="#C44536"
              initial={{ x: origin, y: arrowY, opacity: 0 }}
              animate={
                isInView
                  ? {
                      x:       [origin, origin, wallHit, wallHit, wallHit],
                      opacity: [0,      1,      1,       0,       0],
                    }
                  : { opacity: 0 }
              }
              transition={{
                duration: cyc,
                times:   [0, 0.02, 0.35, 0.37, 1],
                repeat: Infinity,
                delay: stagger,
                ease: 'linear',
              }}
            />

            {/* Incoming line trail */}
            <motion.line
              x1={origin} y1={arrowY} x2={origin} y2={arrowY}
              stroke="#C44536" strokeWidth={2} strokeLinecap="round"
              initial={{ opacity: 0 }}
              animate={
                isInView
                  ? {
                      x2:      [origin, origin, wallHit, wallHit, wallHit],
                      opacity: [0,      1,      1,       0,       0],
                    }
                  : { opacity: 0 }
              }
              transition={{
                duration: cyc,
                times:   [0, 0.02, 0.35, 0.37, 1],
                repeat: Infinity,
                delay: stagger,
                ease: 'linear',
              }}
            />

            {/* Spark burst on impact */}
            <motion.circle
              cx={wallHit + 4} cy={arrowY} r={3}
              fill="#F2CC8F"
              initial={{ scale: 0, opacity: 0 }}
              animate={
                isInView
                  ? {
                      scale:   [0, 0, 3.5, 0, 0],
                      opacity: [0, 0, 1,   0, 0],
                    }
                  : { scale: 0, opacity: 0 }
              }
              transition={{
                duration: cyc,
                times:   [0, 0.33, 0.38, 0.43, 1],
                repeat: Infinity,
                delay: stagger,
              }}
            />

            {/* Reflected arrowhead ← (bounces back) */}
            <motion.polygon
              points="0,-4 -9,0 0,4"
              fill="#C44536"
              initial={{ x: wallHit, y: arrowY, opacity: 0 }}
              animate={
                isInView
                  ? {
                      x:       [wallHit, wallHit, wallHit, origin, origin],
                      opacity: [0,       0,       0.75,    0.3,    0],
                    }
                  : { opacity: 0 }
              }
              transition={{
                duration: cyc,
                times:   [0, 0.37, 0.40, 0.75, 0.80],
                repeat: Infinity,
                delay: stagger,
                ease: 'linear',
              }}
            />

            {/* Reflected line trail */}
            <motion.line
              x1={wallHit} y1={arrowY} x2={wallHit} y2={arrowY}
              stroke="#C44536" strokeWidth={1.5} strokeLinecap="round"
              initial={{ opacity: 0 }}
              animate={
                isInView
                  ? {
                      x2:      [wallHit, wallHit, wallHit, origin, origin],
                      opacity: [0,       0,       0.6,     0.15,   0],
                    }
                  : { opacity: 0 }
              }
              transition={{
                duration: cyc,
                times:   [0, 0.37, 0.40, 0.75, 0.80],
                repeat: Infinity,
                delay: stagger,
                ease: 'linear',
              }}
            />
          </g>
        );
      })}

      {/* Risks label */}
      <motion.text
        x={18}
        y={72}
        fill="#C44536"
        fontSize={9}
        fontFamily="Manrope, sans-serif"
        initial={{ opacity: 0 }}
        animate={isInView ? { opacity: 0.55 } : { opacity: 0 }}
        transition={{ delay: 1.5 }}
      >
        Risks →
      </motion.text>

      {/* "I am here!" on 3rd wall */}
      <motion.text
        x={210}
        y={205}
        textAnchor="middle"
        fill="#81B29A"
        fontSize={10}
        fontWeight={700}
        fontFamily="Manrope, sans-serif"
        initial={{ opacity: 0 }}
        animate={isInView ? { opacity: hovered ? 1 : 0.7 } : { opacity: 0 }}
        transition={{ delay: 2 }}
      >
         ← I am here!
      </motion.text>
    </motion.svg>
  );
}

/* ──────────────────────────────────────────────
   4. INDIC SOUL  –  Lotus center, concentric rings,
   4 cardinal themed points with micro-animations
   ────────────────────────────────────────────── */
function OrbitingSymbol({ angle, char, fill, size, cx, cy, rx, ry }) {
  const rad = (angle * Math.PI) / 180;
  const x = cx + rx * Math.cos(rad);
  const y = cy + ry * Math.sin(rad);
  return (
    <text x={x} y={y} textAnchor="middle" dominantBaseline="central"
      fill={fill} fontSize={size} fontWeight={800}>{char}</text>
  );
}

function AstroOrbitGroup({ isInView, cx, cy, GOLD }) {
  const angle = useMotionValue(0);
  const [angles, setAngles] = useState([0, 120, 240]);

  useEffect(() => {
    if (!isInView) return;
    const ctrl = animate(angle, 360, {
      duration: 5,
      ease: 'linear',
      repeat: Infinity,
    });
    const unsub = angle.on('change', (v) => {
      setAngles([v, v + 120, v + 240]);
    });
    return () => { ctrl.stop(); unsub(); };
  }, [isInView, angle]);

  const symbols = [
    { char: '☀', fill: GOLD, size: 18, a: angles[0] },
    { char: '☽', fill: GOLD, size: 16, a: angles[1] },
    { char: '✦', fill: GOLD, size: 14, a: angles[2] },
  ];

  return (
    <g>
      {symbols.map(({ char, fill, size, a }) => (
        <OrbitingSymbol key={char} angle={a} char={char} fill={fill}
          size={size} cx={cx} cy={cy} rx={28} ry={8} />
      ))}
    </g>
  );
}

function OrbitGroup({ isInView, cx, cy, GOLD, TERRA }) {
  const angle = useMotionValue(0);

  const [angles, setAngles] = useState([0, 120, 240]);

  useEffect(() => {
    if (!isInView) return;
    const ctrl = animate(angle, 360, {
      duration: 5,
      ease: 'linear',
      repeat: Infinity,
    });
    const unsub = angle.on('change', (v) => {
      setAngles([v, v + 120, v + 240]);
    });
    return () => { ctrl.stop(); unsub(); };
  }, [isInView, angle]);

  const symbols = [
    { char: 'अ', fill: GOLD, size: 20, a: angles[0] },
    { char: 'ಅ', fill: TERRA, size: 18, a: angles[1] },
    { char: '♪', fill: '#C4963A', size: 17, a: angles[2] },
  ];

  return (
    <g>
      {symbols.map(({ char, fill, size, a }) => (
        <OrbitingSymbol key={char} angle={a} char={char} fill={fill}
          size={size} cx={cx} cy={cy} rx={28} ry={8} />
      ))}
    </g>
  );
}

function LotusBloomAnimation({ isInView }) {
  const [hovered, setHovered] = useState(false);
  const MID = 190;
  const GOLD = '#F2CC8F';
  const GOLD_DARK = '#D4A017';
  const TERRA = '#E07A5F';
  const petalCount = 12;
  const petalPath = 'M0,-2 C8,-22 14,-50 0,-68 C-14,-50 -8,-22 0,-2Z';

  return (
    <motion.svg
      viewBox="0 0 380 380"
      className="w-full max-w-sm select-none"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* ── Concentric rings — darker, outer ones slowly rotate ── */}
      {[68, 95, 125].map((r, i) => (
        <motion.circle
          key={`ring-${i}`}
          cx={MID} cy={MID} r={r}
          fill="none"
          stroke={GOLD}
          strokeWidth={1.2 + i * 0.4}
          strokeDasharray={i === 0 ? 'none' : `${3 + i * 2} ${5 + i * 2}`}
          opacity={0.4 + i * 0.1}
          initial={{ scale: 0 }}
          animate={isInView ? { scale: 1, rotate: i > 0 ? [0, 360] : 0 } : { scale: 0 }}
          transition={{
            scale: { duration: 0.5, delay: 0.15 + i * 0.12 },
            rotate: i > 0 ? { repeat: Infinity, duration: 30 + i * 20, ease: 'linear' } : undefined,
          }}
          style={{ transformOrigin: `${MID}px ${MID}px` }}
        />
      ))}

      {/* Inner glow pulse behind lotus */}
      <motion.circle cx={MID} cy={MID} r={30} fill="rgba(242,204,143,0.1)"
        animate={isInView ? { r: [26, 35, 26], opacity: [0.08, 0.2, 0.08] } : { r: 0 }}
        transition={{ repeat: Infinity, duration: 3, ease: 'easeInOut' }} />

      {/* ── Lotus petals — spinning around center ── */}
      <motion.g
        animate={isInView ? { rotate: [0, 360] } : {}}
        transition={{ repeat: Infinity, duration: 3, ease: 'linear' }}
        style={{ transformOrigin: `${MID}px ${MID}px` }}>
        {Array.from({ length: petalCount }).map((_, i) => {
          const a = (360 / petalCount) * i;
          return (
            <g key={`p-${i}`} transform={`translate(${MID},${MID}) rotate(${a})`}>
              <motion.path
                d={petalPath}
                fill="rgba(242,204,143,0.35)"
                stroke={GOLD}
                strokeWidth={1}
                initial={{ scale: 0, opacity: 0 }}
                animate={isInView ? { scale: 1, opacity: 1 } : { scale: 0, opacity: 0 }}
                transition={{ duration: 0.5, delay: 0.4 + i * 0.06 }}
                style={{ transformOrigin: '0px 0px' }}
              />
            </g>
          );
        })}
      </motion.g>

      {/* Static center focal point */}
      <g transform={`translate(${MID},${MID})`}>
        <motion.circle cx={0} cy={0} r={10} fill="rgba(242,204,143,0.25)"
          initial={{ scale: 0 }} animate={isInView ? { scale: 1 } : { scale: 0 }}
          transition={{ duration: 0.3, delay: 0.3 }} />
        <motion.circle cx={0} cy={0} r={7} fill={GOLD} opacity={0.85}
          initial={{ scale: 0 }} animate={isInView ? { scale: 1 } : { scale: 0 }}
          transition={{ duration: 0.3, delay: 0.33 }} />
        <motion.circle cx={0} cy={0} r={3.5} fill={TERRA} opacity={0.9}
          initial={{ scale: 0 }} animate={isInView ? { scale: 1 } : { scale: 0 }}
          transition={{ duration: 0.25, delay: 0.38 }} />
      </g>

      {/* ════════════════════════════════════════════
          1. LEFT — Astronomy: sun, moon, star
          ════════════════════════════════════════════ */}
      <motion.g initial={{ opacity: 0 }} animate={isInView ? { opacity: 1 } : {}}
        transition={{ delay: 0.8 }}>
        {/* Sun, moon, star orbiting sideways */}
        <AstroOrbitGroup isInView={isInView} cx={55} cy={188} GOLD={GOLD} />

        {/* Label */}
        <motion.text x={55} y={214} textAnchor="middle" fill={GOLD_DARK} fontSize={11}
          fontWeight={800} fontFamily="Manrope, sans-serif"
          initial={{ opacity: 0, x: -12 }}
          animate={isInView ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.6, delay: 1.2 }}>
          Astronomy
        </motion.text>
      </motion.g>

      {/* ════════════════════════════════════════════
          2. TOP — History & Architecture: Hoysala emblem image
          ════════════════════════════════════════════ */}
      <motion.g initial={{ opacity: 0 }} animate={isInView ? { opacity: 1 } : {}}
        transition={{ delay: 1.0 }}>
        {/* Glow behind emblem — static */}
        <motion.circle cx={MID} cy={55} r={48} fill={GOLD} opacity={0.22}
          initial={{ scale: 0 }}
          animate={isInView ? { scale: 1 } : { scale: 0 }}
          transition={{ duration: 0.5, delay: 1.0 }} />

        {/* Hoysala emblem image — gentle float */}
        <motion.g
          animate={isInView ? { y: [0, -3, 0, 3, 0] } : {}}
          transition={{ repeat: Infinity, duration: 4, ease: 'easeInOut' }}>
          <image
            href="/assets/hoysala-emblem.png"
            x={MID - 37.5} y={17}
            width={75} height={75}
            opacity={0.9}
            preserveAspectRatio="xMidYMid meet"
            style={{ mixBlendMode: 'multiply' }}
          />
        </motion.g>

        {/* Label */}
        <motion.text x={MID} y={105} textAnchor="middle" fill={GOLD_DARK} fontSize={11}
          fontWeight={800} fontFamily="Manrope, sans-serif"
          initial={{ opacity: 0, y: -10 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 1.4 }}>
          History & Architecture
        </motion.text>
      </motion.g>

      {/* ════════════════════════════════════════════
          3. RIGHT — Music, Language & Literature
          ════════════════════════════════════════════ */}
      <motion.g initial={{ opacity: 0 }} animate={isInView ? { opacity: 1 } : {}}
        transition={{ delay: 1.2 }}>
        {/* Three symbols orbiting gently around a shared center */}
        <OrbitGroup isInView={isInView} cx={335} cy={188} GOLD={GOLD} TERRA={TERRA} />

        {/* Labels */}
        <motion.text x={335} y={218} textAnchor="middle" fill={GOLD_DARK} fontSize={10.5}
          fontWeight={800} fontFamily="Manrope, sans-serif"
          initial={{ opacity: 0, x: 12 }}
          animate={isInView ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.6, delay: 1.6 }}>
          Music, Language
        </motion.text>
        <motion.text x={335} y={230} textAnchor="middle" fill={GOLD_DARK} fontSize={10.5}
          fontWeight={800} fontFamily="Manrope, sans-serif"
          initial={{ opacity: 0, x: 12 }}
          animate={isInView ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.6, delay: 1.7 }}>
          & Literature
        </motion.text>
      </motion.g>

      {/* ════════════════════════════════════════════
          4. BOTTOM — Theology: Sri Yantra
          ════════════════════════════════════════════ */}
      <motion.g initial={{ opacity: 0 }} animate={isInView ? { opacity: 1 } : {}}
        transition={{ delay: 1.4 }}>
        {/* Background circle */}
        <circle cx={MID} cy={315} r={30} fill={GOLD} opacity={0.15} />

        {/* Sri Yantra — gentle float */}
        <motion.g
          animate={isInView ? { y: [0, -2, 0, 2, 0] } : {}}
          transition={{ repeat: Infinity, duration: 4, ease: 'easeInOut' }}>
          <g transform={`translate(${MID}, 315)`}>
            {/* Outer circle */}
            <circle cx={0} cy={0} r={28} fill="none" stroke={GOLD} strokeWidth={1.2} opacity={0.5} />
            <circle cx={0} cy={0} r={25} fill="none" stroke={GOLD} strokeWidth={0.6} opacity={0.35} />

            {/* Downward triangles (Shakti — feminine) */}
            <path d="M0,-20 L17.3,10 L-17.3,10 Z" fill="none" stroke={GOLD} strokeWidth={1} opacity={0.8} />
            <path d="M0,-15 L13,7.5 L-13,7.5 Z" fill="none" stroke={GOLD} strokeWidth={0.8} opacity={0.6} />
            <path d="M0,-10 L8.7,5 L-8.7,5 Z" fill="none" stroke={GOLD} strokeWidth={0.7} opacity={0.5} />

            {/* Upward triangles (Shiva — masculine) */}
            <path d="M0,20 L17.3,-10 L-17.3,-10 Z" fill="none" stroke={TERRA} strokeWidth={1} opacity={0.8} />
            <path d="M0,15 L13,-7.5 L-13,-7.5 Z" fill="none" stroke={TERRA} strokeWidth={0.8} opacity={0.6} />
            <path d="M0,10 L8.7,-5 L-8.7,-5 Z" fill="none" stroke={TERRA} strokeWidth={0.7} opacity={0.5} />

            {/* Bindu — the central point */}
            <motion.circle cx={0} cy={0} r={2.5} fill={GOLD} opacity={0.9}
              animate={isInView ? { r: [2.5, 3.2, 2.5], opacity: [0.9, 1, 0.9] } : {}}
              transition={{ repeat: Infinity, duration: 2.5, ease: 'easeInOut' }} />

            {/* Lotus petal ring — 8 small petals around the yantra */}
            {Array.from({ length: 8 }).map((_, i) => {
              const angle = (i * 45) * Math.PI / 180;
              const px = Math.cos(angle) * 22;
              const py = Math.sin(angle) * 22;
              return (
                <circle key={`yp-${i}`} cx={px} cy={py} r={1.5}
                  fill={GOLD} opacity={0.35} />
              );
            })}
          </g>
        </motion.g>

        {/* Label */}
        <motion.text x={MID} y={353} textAnchor="middle" fill={GOLD_DARK} fontSize={11}
          fontWeight={800} fontFamily="Manrope, sans-serif"
          initial={{ opacity: 0, y: 10 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 1.8 }}>
          Theology
        </motion.text>
      </motion.g>
    </motion.svg>
  );
}

/* ──────────────────────────────────────────────
   DIMENSION ROW – animation + text, alternating
   ────────────────────────────────────────────── */
const animationMap = {
  tech: VennDiagramAnimation,
  'rabbit-hole': RabbitHoleAnimation,
  audit: ThirdLineAnimation,
  indic: LotusBloomAnimation,
};

function DimensionRow({ dimension, index }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });
  const reversed = index % 2 !== 0;
  const Anim = animationMap[dimension.id];

  return (
    <div
      ref={ref}
      data-testid={`dimension-card-${dimension.id}`}
      className={`flex flex-col ${
        reversed ? 'md:flex-row-reverse' : 'md:flex-row'
      } items-center gap-6 md:gap-12 py-10 md:py-14`}
    >
      {/* Animation */}
      <div className="w-full md:w-1/2 flex justify-center">
        <Anim isInView={isInView} />
      </div>

      {/* Text */}
      <motion.div
        className="w-full md:w-1/2 space-y-3"
        initial={{ opacity: 0, x: reversed ? -40 : 40 }}
        animate={isInView ? { opacity: 1, x: 0 } : {}}
        transition={{ duration: 0.8, delay: 0.3 }}
      >
        <motion.span
          className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground font-body inline-block"
          initial={{ opacity: 0, letterSpacing: '0.05em' }}
          animate={isInView ? { opacity: 1, letterSpacing: '0.2em' } : {}}
          transition={{ duration: 1, delay: 0.5 }}
        >
          {dimension.subtitle}
        </motion.span>
        <motion.h3
          className={`font-heading text-2xl md:text-3xl font-semibold ${dimension.textClass}`}
          initial={{ opacity: 0, y: 12 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, delay: 0.7 }}
        >
          {dimension.title}
        </motion.h3>
        <motion.p
          className="font-body text-muted-foreground leading-relaxed text-sm"
          initial={{ opacity: 0, y: 8 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, delay: 0.9 }}
        >
        {dimension.description}
        </motion.p>
        {dimension.link.startsWith('/') ? (
          <motion.div
            initial={{ opacity: 0, x: -10 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 1.1 }}
            whileHover={{ x: 5 }}
          >
            <Link
              to={dimension.link}
              className={`inline-flex items-center gap-2 ${dimension.textClass} font-body text-sm font-medium pt-2 group`}
            >
              <span className="uppercase tracking-widest text-xs">{dimension.linkLabel || 'Explore'}</span>
              <svg
                className="w-4 h-4 group-hover:translate-x-2 transition-transform"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 8l4 4m0 0l-4 4m4-4H3"
                />
              </svg>
            </Link>
          </motion.div>
        ) : (
          <motion.a
            href={dimension.link}
            className={`inline-flex items-center gap-2 ${dimension.textClass} font-body text-sm font-medium pt-2 group`}
            initial={{ opacity: 0, x: -10 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 1.1 }}
            whileHover={{ x: 5 }}
          >
            <span className="uppercase tracking-widest text-xs">{dimension.linkLabel || 'Explore'}</span>
            <svg
              className="w-4 h-4 group-hover:translate-x-2 transition-transform"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 8l4 4m0 0l-4 4m4-4H3"
              />
            </svg>
          </motion.a>
        )}
      </motion.div>
      </div>
  );
}

/* ──────────────────────────────────────────────
   MAIN SECTION
   ────────────────────────────────────────────── */
export function DimensionsSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section 
      id="dimensions"
      data-testid="dimensions-section"
      ref={ref}
      className="relative py-20 md:py-28 px-6 bg-background"
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="mb-8 max-w-3xl"
        >
          <motion.span
            className="text-xs font-bold uppercase text-primary font-body inline-block"
            initial={{ opacity: 0, letterSpacing: '0.1em' }}
            animate={isInView ? { opacity: 1, letterSpacing: '0.3em' } : {}}
            transition={{ duration: 1.2, delay: 0.1 }}
          >
            Highlighted Dimensions
          </motion.span>
          <motion.h2
            className="font-heading text-4xl md:text-5xl font-semibold text-foreground mt-4"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            The <span className="text-primary italic">many facets</span> of who
            I am
          </motion.h2>
          <p className="font-body text-muted-foreground mt-6 text-lg">
            Four pillars that define my journey, each one a rabbit hole worth
            exploring.
          </p>
        </motion.div>

        {/* Dimension rows */}
        <div className="divide-y divide-border/40">
          {dimensions.map((dimension, index) => (
            <DimensionRow
              key={dimension.id}
              dimension={dimension}
              index={index}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
